import React, { Suspense } from "react";
import {
  BrowserRouter,
  MemoryRouter,
  Routes,
  Route,
  Outlet,
} from "react-router-dom";
import { routes } from "./routes";
import { HelmetProvider } from "react-helmet-async";
import { TrackingInjector } from "@/components/shared/TrackingInjector";
import { SiteSettingsProvider } from "@/contexts/SiteSettingsContext";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { RegionProvider, getLocaleConfig } from "@/contexts/RegionContext";
import { stripLocalePrefix } from "@/lib/publicBaseUrl";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { InitialDataProvider } from "@/contexts/InitialDataContext";
import { AdminGate } from "@/components/admin/AdminGate";
import { SEOHead } from "@/components/shared/SEOHead";
import { StickyCTA } from "@/components/shared/StickyCTA";
import { CookieConsent } from "@/components/shared/CookieConsent";
import { ScrollToTop } from "@/components/shared/ScrollToTop";
import BackToTop from "@/components/shared/BackToTop";
import AccessibilityToggle from "@/components/shared/AccessibilityToggle";
import { Chatbot } from "@/components/chat/Chatbot";
import { ErrorBoundary } from "@/components/shared/ErrorBoundary";
import { GlobalBreadcrumbs } from "@/components/layout/GlobalBreadcrumbs";

const MainLayout = () => {
  return (
    <>
      <div className="flex-1 flex flex-col">
        <Outlet />
      </div>
    </>
  );
};

interface AppProps {
  serverUrl?: string;
  helmetContext?: any;
  initialData?: any;
  initialGeo?: any;
}

import { Toaster } from 'react-hot-toast';

export default function App({ serverUrl, helmetContext = {}, initialData, initialGeo }: AppProps) {
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);

  const effectiveData = initialData !== undefined ? initialData : (typeof window !== 'undefined' ? (window as any).__INITIAL_DATA__ : undefined);
  const effectiveGeo = initialGeo !== undefined ? initialGeo : (typeof window !== 'undefined' ? (window as any).__GEO_INITIAL__ : undefined);

  const Router = serverUrl ? MemoryRouter : BrowserRouter;
  const { basename } = getLocaleConfig(serverUrl);
  const effectiveServerUrl = serverUrl ? stripLocalePrefix(serverUrl) : undefined;
  const routerProps = effectiveServerUrl ? { initialEntries: [effectiveServerUrl] } : { basename };

  return (
    <HelmetProvider context={helmetContext}>
      <InitialDataProvider initialData={effectiveData}>
        <TrackingInjector />
        <SiteSettingsProvider>
          <RegionProvider serverUrl={serverUrl} initialGeo={effectiveGeo}>
            <Router {...routerProps}>
              <AdminAuthProvider>
                {mounted && (
                  <Toaster 
                    position="top-center"
                    toastOptions={{
                      className: 'font-sans text-sm border border-ground-deep shadow-lg rounded-none',
                      style: {
                        background: 'var(--ground-alt)', // ground-alt
                        color: 'var(--ink)', // ink
                      },
                      error: {
                        style: {
                          borderLeft: '4px solid #ef4444' // red-500
                        },
                      }
                    }}
                  />
                )}
                <SEOHead />
                <ScrollToTop />
                <div className="min-h-screen bg-ground text-ink selection:bg-rule-3/30 selection:text-ink relative">
                {/* Global Background Motif */}
                <div
                  className="fixed inset-0 pointer-events-none z-0 opacity-[0.03]"
                  style={{
                    backgroundImage: "url(/fingerprint.webp)",
                    backgroundSize: "100% auto",
                    backgroundPosition: "top right",
                    backgroundRepeat: "repeat-y",
                  }}
                />
                <div className="relative z-10">
                  <Navbar />
                  <GlobalBreadcrumbs />
                  <StickyCTA />
                  <ErrorBoundary>
                  <Suspense fallback={<div className="min-h-screen bg-ground-alt flex items-center justify-center"><div className="w-8 h-8 border-4 border-rule-3 border-t-transparent rounded-full animate-spin"></div></div>}>
                  <Routes>
                    <Route element={<MainLayout />}>
                      {routes
                        .filter((route) => !route.standalone)
                        .map((route) => (
                          <Route
                            key={route.path}
                            path={route.path}
                            element={
                              route.path.startsWith('/admin') ? (
                                <AdminGate>
                                  <route.component />
                                </AdminGate>
                              ) : (
                                <route.component />
                              )
                            }
                          />
                        ))}
                    </Route>
                    {routes
                      .filter((route) => route.standalone)
                      .map((route) => (
                        <Route
                          key={route.path}
                          path={route.path}
                          element={
                            route.path.startsWith('/admin') ? (
                              <AdminGate>
                                <route.component />
                              </AdminGate>
                            ) : (
                              <route.component />
                            )
                          }
                        />
                      ))}
                  </Routes>
                  </Suspense>
                  </ErrorBoundary>
                  <Footer />
                  <CookieConsent />
                  <Chatbot />
                  <AccessibilityToggle />
                  <BackToTop />
                </div>
              </div>
              </AdminAuthProvider>
            </Router>
          </RegionProvider>
        </SiteSettingsProvider>
      </InitialDataProvider>
    </HelmetProvider>
  );
}
